import type { IStreamClaraAnswerParams } from "../../../api/features/clara-stream/clara-stream.api";
import { streamClaraAnswer } from "../../../api/features/clara-stream/clara-stream.api";
import { HttpStatusCode } from "../../../legacy/common/http-status-code";
import { QuotaExceededError } from "../../../legacy/components/Chatbot/errors/quota-exceeded.error";
import { RateLimitExceededError } from "../../../legacy/components/Chatbot/errors/rate-limit-exceeded.error";
import type { AskToChatbotParams } from "../../../legacy/components/Chatbot/lib/mediator/chatbot.mediator.interface";
import type { IChatbotDispatcherService } from "../../../legacy/components/Chatbot/lib/services/chatbot-dispatcher/chatbot.dispatcher.service.interface";
import { ChatbotMessageHandlerService } from "../../../legacy/components/Chatbot/lib/services/chatbot-message-handler/chatbot-message-handler.service";
import type { IChatbotMessageHandlerService } from "../../../legacy/components/Chatbot/lib/services/chatbot-message-handler/chatbot-message-handler.service.interface";
import { UserMessageAppendStrategy } from "../../../legacy/components/Chatbot/services/chatbot-stream-message-handler/chatbot-stream-message-handler.enum";
import type { IChatbotMessageStreamHandler } from "../../../legacy/components/Chatbot/services/chatbot-stream-message-handler/chatbot-stream-message-handler.interface";
import { AuthenticationService } from "../../../legacy/services/authentication-service/authentication.service";
import type { IAuthenticationService } from "../../../legacy/services/authentication-service/authentication.service.interface";
import type { AiChatbotChunkEntity } from "../../../legacy/services/server-side-events-ai-chatbot/entities/ai-chatbot-chunk.entity";
import { ClaraStreamHttpError } from "../types/clara-stream-http.error";
import { ClaraStreamInterruptedError } from "../types/clara-stream-interrupted.error";

function hasHttpStatus(error: unknown, status: HttpStatusCode): boolean {
  return error instanceof ClaraStreamHttpError && error.status === status;
}

function isRateLimitFailure(error: unknown): boolean {
  const isInterrupted = error instanceof ClaraStreamInterruptedError;
  if (!isInterrupted) {
    return false;
  }

  return RateLimitExceededError.isRateLimitExceeded(error.reason) || QuotaExceededError.isQuotaExceeded(error.reason);
}

export class Clara2MessageStreamHandler implements IChatbotMessageStreamHandler {
  constructor(
    private readonly _chatbotDispatcherService: IChatbotDispatcherService,
    private readonly _chatbotMessageHandlerService: IChatbotMessageHandlerService = new ChatbotMessageHandlerService(),
    private readonly _streamAnswer: (params: IStreamClaraAnswerParams) => Promise<void> = streamClaraAnswer,
    private readonly _authenticationService: IAuthenticationService = new AuthenticationService()
  ) {}

  public async processMessage(
    params: AskToChatbotParams,
    appendStrategy: UserMessageAppendStrategy = UserMessageAppendStrategy.APPEND
  ): Promise<void> {
    this._chatbotDispatcherService.setIsSendingAction(true);
    this._chatbotDispatcherService.setShowEmptyState(false);

    const userMessage = this._chatbotMessageHandlerService.generateUserMessage(params.message);
    const shouldAppendUserMessage = appendStrategy === UserMessageAppendStrategy.APPEND;
    if (shouldAppendUserMessage) {
      this._chatbotDispatcherService.appendUserMessage(userMessage);
    }

    try {
      await this._streamAnswer({ question: userMessage.text, onChunk: this._handleChunk.bind(this) });
    } catch (error) {
      await this._handleFailure(error);
    } finally {
      this._chatbotDispatcherService.setIsSendingAction(false);
      this._chatbotDispatcherService.setEndStreamingResponse();
    }
  }

  private _handleChunk(chunk: AiChatbotChunkEntity): void {
    const assistantMessage = this._chatbotMessageHandlerService.generateAssistantMessage(chunk.payload);

    if (chunk.isFirstChunk) {
      this._chatbotDispatcherService.appendAiMessage(assistantMessage);
      this._chatbotDispatcherService.setIsSendingAction(false);
      this._chatbotDispatcherService.setStartStreamingResponse();
      return;
    }

    this._chatbotDispatcherService.appendAiMessageChunk({ ...assistantMessage, id: chunk.messageId });
  }

  private async _handleFailure(error: unknown): Promise<void> {
    const isUnauthorized = hasHttpStatus(error, HttpStatusCode.UNAUTHORIZED);
    if (isUnauthorized) {
      await this._authenticationService.logoutAndResetStorage();
      return;
    }

    const isRateLimited = isRateLimitFailure(error);
    if (isRateLimited) {
      this._chatbotDispatcherService.setErrorMessage(RateLimitExceededError.create().message);
      this._chatbotDispatcherService.setRateLimitExceeded(true);
      this._chatbotDispatcherService.setShowRetryButton(false);
      return;
    }

    this._chatbotDispatcherService.setErrorWhileGeneratingResponse(true);

    const isNotAvailableForAccount = hasHttpStatus(error, HttpStatusCode.FORBIDDEN);
    if (isNotAvailableForAccount) {
      this._chatbotDispatcherService.setShowRetryButton(false);
    }
  }
}
